/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

export enum PricingTier {
	Monthly = 'monthly',
	Yearly = 'yearly',
}

export enum Currency {
	USD = 'USD',
	EUR = 'EUR',
	BRL = 'BRL',
	INR = 'INR',
	PLN = 'PLN',
	TRY = 'TRY',
}

export type PricingMode = 'localized' | 'base';

const EEA_COUNTRIES = [
	'AT',
	'BE',
	'BG',
	'HR',
	'CY',
	'CZ',
	'DK',
	'EE',
	'FI',
	'FR',
	'DE',
	'GR',
	'HU',
	'IE',
	'IT',
	'LV',
	'LT',
	'LU',
	'MT',
	'NL',
	'PL',
	'PT',
	'RO',
	'SK',
	'SI',
	'ES',
	'SE',
	'IS',
	'LI',
	'NO',
];

// Explicit localized prices anchored to the USD price via spot FX and then rounded to "nice"
// display amounts. FX reference date: 2026-04-15.
const PRICE: Record<PricingTier, Record<Currency, number>> = {
	[PricingTier.Monthly]: {
		[Currency.USD]: 4.99,
		[Currency.EUR]: 4.99,
		[Currency.BRL]: 24.99,
		[Currency.INR]: 499,
		[Currency.PLN]: 17.99,
		[Currency.TRY]: 229.99,
	},
	[PricingTier.Yearly]: {
		[Currency.USD]: 49.99,
		[Currency.EUR]: 49.99,
		[Currency.BRL]: 249.99,
		[Currency.INR]: 4999,
		[Currency.PLN]: 179.99,
		[Currency.TRY]: 2299.99,
	},
};

function getCurrency(countryCode: string | null): Currency {
	if (!countryCode) return Currency.USD;
	const upperCountryCode = countryCode.toUpperCase();
	if (upperCountryCode === 'BR') return Currency.BRL;
	if (upperCountryCode === 'IN') return Currency.INR;
	if (upperCountryCode === 'PL') return Currency.PLN;
	if (upperCountryCode === 'TR') return Currency.TRY;
	return isEEACountry(countryCode) ? Currency.EUR : Currency.USD;
}

function isEEACountry(countryCode: string): boolean {
	return EEA_COUNTRIES.includes(countryCode.toUpperCase());
}

export function hasLocalizedPricingChoice(countryCode: string | null | undefined): boolean {
	if (!countryCode) return false;
	const upperCountryCode = countryCode.toUpperCase();
	return upperCountryCode === 'BR' || upperCountryCode === 'IN' || upperCountryCode === 'PL' || upperCountryCode === 'TR';
}

export function getBaseCurrency(countryCode: string | null | undefined): Currency {
	if (!countryCode) return Currency.USD;
	return isEEACountry(countryCode) ? Currency.EUR : Currency.USD;
}

export function isLocalizedCurrency(currency: string | null | undefined): boolean {
	return currency === Currency.BRL || currency === Currency.INR || currency === Currency.PLN || currency === Currency.TRY;
}

export function getCurrencyCodeLabel(currency: string | null | undefined): string {
	switch (currency) {
		case Currency.BRL:
			return 'BRL';
		case Currency.INR:
			return 'INR';
		case Currency.PLN:
			return 'PLN';
		case Currency.TRY:
			return 'TRY';
		case Currency.EUR:
			return 'EUR';
		case Currency.USD:
		default:
			return 'USD';
	}
}

function getPrice(tier: PricingTier, currency: Currency): number {
	return PRICE[tier][currency];
}

function formatPrice(price: number, currency: Currency): string {
	const currencySymbols: Record<Currency, string> = {
		[Currency.USD]: '$',
		[Currency.EUR]: '€',
		[Currency.BRL]: 'R$',
		[Currency.INR]: '₹',
		[Currency.PLN]: 'zł',
		[Currency.TRY]: '₺',
	};

	return `${currencySymbols[currency]}${price.toFixed(2).replace(/\.00$/, '')}`;
}

export function getFormattedPrice(tier: PricingTier, countryCode: string | null): string {
	const currency = getCurrency(countryCode);
	const price = getPrice(tier, currency);
	return formatPrice(price, currency);
}

export function formatMinorUnitPrice(
	amountMinor: number | null | undefined,
	currency: string | null | undefined,
	locale: string,
): string | null {
	if (amountMinor == null || !currency) {
		return null;
	}

	const fractionDigits = new Intl.NumberFormat(locale, {
		style: 'currency',
		currency,
	}).resolvedOptions().maximumFractionDigits ?? 2;

	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency,
		minimumFractionDigits: 0,
		maximumFractionDigits: fractionDigits,
	}).format(amountMinor / 10 ** fractionDigits);
}
